/**
 * THE PLAN CARD — what the funding card became (ruling R-0916-1).
 *
 * The funding card (wave 9a) met a sender who had run out of credits, told them
 * how far short they were, and sold them a pack. There are no credits, no
 * shortfall and no packs. What a person who has run out meets now is the roads
 * ToU 2.2 §5.3 names, in the order the product wants them taken:
 *
 *   1. **wait** — the allowance resets when the next window falls due, and the
 *      surface around the card says when (since wave 61 the card does not say
 *      it a second time);
 *   2. **move up** — the rung above this one, priced;
 *   3. **pay as you go** — beside it, where the paid reset used to be (ruling
 *      R-0923-3): turning it on at the allowance, raising the maximum at the
 *      maximum.
 *
 * On FREE and during a TRIAL the single road
 * is "start the plan", and the card never works that out for itself: the
 * server's refusal carries `start_plan`.
 *
 * **ONE CARD, AND NEVER IN A CONVERSATION** (owner, 2026-09-23; wave 57). It
 * is rendered where prices belong — the plan view and the plan panel's index —
 * and nowhere else (the paywall that also carried it is Free's alone since wave
 * 61). A conversation shows one control, "Get more usage", and that control
 * opens the plan view on this card.
 *
 * **NO CAPACITY, EVER** (ruling C2). A price is allowed — the auto-renewal
 * rules require the amount in visual proximity to the control that agrees to
 * it — but no quantity of AI work appears anywhere on this card, and no
 * allowance floor either (ruling R-0916-2). What was refused is never priced:
 * that is the per-action price this product retired.
 */
import type { LadderRung, PaygView, PlanTier } from "../../lib/api";
import { QuietButton } from "../ui/button";
import { PaygPanel } from "./payg";
import { UpgradeOffer } from "./upgrade-offer";

/** What the card is, for anyone not reading it visually. */
export const PLAN_CARD_LABEL = "Your plan";

/**
 * A period that has been used up, in a knock's thread (`knock/thread.tsx`). No
 * figure, and no capacity. The card itself no longer leads with it (wave 61):
 * the surfaces it sits on say it in their own status line.
 */
export const PLAN_SPENT_LEAD = "This month's allowance is used up.";

/** The lead on a tier that never had one (ruling C10) and on a trial (§8.3). */
export const PLAN_START_LEAD = "Knocking and talking to your doorman both take a plan.";

export const START_PLAN_ACTION = "See the plans";

export type PlanCardProps = {
  /** The rung above this one, or null at the top of the ladder. */
  nextTier: PlanTier | null;
  /** The ladder, so the rung above can be named and priced. */
  ladder: LadderRung[];
  /** True on Free and during a trial: the one road is "start the plan". */
  startPlan: boolean;
  /** Open the plans pane. Absent means the card carries no plan action. */
  onSeePlans?: (() => void) | undefined;
  /**
   * PAY AS YOU GO, where "Get more usage" offers it (ruling R-0923-3 (3)):
   * turning it on at the allowance, raising the maximum at the maximum. Absent
   * where the surface has its own pay-as-you-go settings beside the card, and
   * null on a trial or Free, which are never offered it.
   */
  payg?: PaygView | null;
  /** What the prices include — "incl. VAT" — or null (ruling R-0923-5). */
  priceSuffix?: string | null;
};

export function PlanCard({
  nextTier,
  ladder,
  startPlan,
  onSeePlans,
  payg,
  priceSuffix,
}: PlanCardProps) {
  return (
    <div
      className="mt-4 rounded-lg border border-border bg-surface px-4 py-3 [&>*:first-child]:mt-0"
      data-plan-card
    >
      {/* NO LEAD AND NO DATE OVER A SPENT PERIOD (owner, 2026-09-23; wave 61).
          Both surfaces this card sits on already say, in their own status
          line, that the allowance is used and when it resets; the card said
          both again directly underneath. It starts with the options. */}
      {startPlan && <p className="text-sm text-text-soft">{PLAN_START_LEAD}</p>}

      {/* The rung above, priced (§5.4): a product that answers "you have run
          out" with "buy another month of the same" teaches people to sit on
          the smallest plan forever. */}
      {nextTier && (
        <UpgradeOffer
          nextTier={nextTier}
          ladder={ladder}
          priceSuffix={priceSuffix ?? null}
          {...(onSeePlans ? { onSeePlans } : {})}
        />
      )}

      {startPlan && !nextTier && onSeePlans && (
        <p className="mt-3">
          <QuietButton type="button" onClick={onSeePlans}>
            {START_PLAN_ACTION}
          </QuietButton>
        </p>
      )}

      {/* PAY AS YOU GO, BESIDE THE RUNG AND AFTER IT (FR-5.7, docs/plans.md
          §13 Q15): upgrade is the first road where a higher tier exists, and
          pay as you go stands next to it with no acknowledgement beyond its
          own consent. On Pro 20× there is no rung above and it is the offer. */}
      {!startPlan && payg && <PaygPanel payg={payg} mode="offer" />}
    </div>
  );
}
